import React from "react";
import { Package, Palette, Hash, Barcode, Copy } from "lucide-react";
import { toast } from "sonner";

export interface ProductCardData {
  id?: string;
  product_name: string;
  product_sku: string;
  product_bc: string;
  product_ean?: string | null;
  product_color?: string | null;
  product_quantity: number;
  match_notes?: string | null;
  image_url?: string | null;
  vo_number?: string | null;
}

interface NotificationProductCardsProps {
  products: ProductCardData[];
}

const copyToClipboard = (text: string, label: string) => {
  navigator.clipboard.writeText(text)
    .then(() => toast.success(`${label} gekopieerd`))
    .catch(() => toast.error(`Kon ${label} niet kopiëren`));
};

const CopyableText = ({ text, label, children }: { text: string; label: string; children: React.ReactNode }) => (
  <button
    type="button"
    onClick={() => copyToClipboard(text, label)}
    className="inline-flex items-center gap-1 hover:text-primary cursor-pointer transition-colors group"
    title={`Klik om ${label} te kopiëren`}
  >
    {children}
    <Copy className="h-3 w-3 opacity-0 group-hover:opacity-60 transition-opacity" />
  </button>
);

const ProductCard = ({ product, index }: { product: ProductCardData; index: number }) => (
  <div className="flex gap-4 rounded-lg border bg-card p-3">
    <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-md border bg-muted">
      {product.image_url ? (
        <img
          src={product.image_url}
          alt={product.product_name}
          className="h-full w-full object-contain"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = "none";
            (e.target as HTMLImageElement).parentElement!.classList.add("flex", "items-center", "justify-center");
            const icon = document.createElement("div");
            icon.innerHTML = "📦";
            icon.className = "text-2xl";
            (e.target as HTMLImageElement).parentElement!.appendChild(icon);
          }}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-2xl text-muted-foreground">
          📦
        </div>
      )}
    </div>
    <div className="flex-1 min-w-0 space-y-1">
      <div className="flex items-center gap-2">
        <CopyableText text={product.product_name} label="Productnaam">
          <p className="font-medium text-sm leading-tight truncate">
            {product.product_name}
          </p>
        </CopyableText>
        {product.vo_number && (
          <span className="flex-shrink-0 text-xs font-semibold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-md">
            {product.vo_number}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <CopyableText text={product.product_sku} label="Artikelnummer">
          <span className="flex items-center gap-1">
            <Hash className="h-3 w-3" />
            SKU: {product.product_sku}
          </span>
        </CopyableText>
        {product.product_bc && (
          <CopyableText text={product.product_bc} label="Scancode">
            <span className="flex items-center gap-1">
              <Barcode className="h-3 w-3" />
              BC: {product.product_bc}
            </span>
          </CopyableText>
        )}
        {product.product_ean && (
          <span className="flex items-center gap-1">
            EAN: {product.product_ean}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span>Aantal: {product.product_quantity}</span>
        {product.product_color && (
          <span className="flex items-center gap-1">
            <Palette className="h-3 w-3" />
            {product.product_color}
          </span>
        )}
      </div>
      {product.match_notes && (
        <p className="text-xs text-muted-foreground mt-1 italic border-l-2 border-muted pl-2">
          {product.match_notes}
        </p>
      )}
    </div>
  </div>
);

const NotificationProductCards = ({ products }: NotificationProductCardsProps) => {
  if (!products || products.length === 0) return null;

  // Check if products have VO numbers for grouping
  const hasVoNumbers = products.some(p => p.vo_number);

  if (hasVoNumbers) {
    const grouped = products.reduce<Record<string, ProductCardData[]>>((acc, product) => {
      const vo = product.vo_number || "Onbekend";
      if (!acc[vo]) acc[vo] = [];
      acc[vo].push(product);
      return acc;
    }, {});

    return (
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Package className="h-4 w-4" />
          Producten ({products.length})
        </h3>
        {Object.entries(grouped).map(([vo, voProducts]) => (
          <div key={vo} className="space-y-2">
            <p className="text-sm font-semibold text-blue-800 bg-blue-50 px-2 py-1 rounded">{vo}</p>
            {voProducts.map((product, index) => (
              <ProductCard key={product.id || `${vo}-${index}`} product={product} index={index} />
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
        <Package className="h-4 w-4" />
        Producten ({products.length})
      </h3>
      <div className="space-y-3">
        {products.map((product, index) => (
          <ProductCard key={product.id || index} product={product} index={index} />
        ))}
      </div>
    </div>
  );
};

export default NotificationProductCards;
